import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Avatar,
  Button,
  Spin,
  message,
  Typography,
  Divider,
  Space,
  Input,
  Form,
  Tag,
  Grid,
  Descriptions,
  Badge,
  Statistic
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CrownOutlined,
  SafetyCertificateOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  TeamOutlined,
  DashboardOutlined,
  SettingOutlined,
  ClockCircleOutlined
} from "@ant-design/icons";
import profilavatar from "../assets/images/face-1.jpg";
import { meApi } from "../api/auth.api";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { TextArea } = Input;

function Profile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const screens = useBreakpoint();

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const response = await meApi();

      if (response.data?.status === "success" && response.data.data?.user) {
        const userData = response.data.data.user;
        setUser(userData);
        setFormData({
          full_name: userData.full_name || "",
          email: userData.email || "",
          phone_number: userData.phone_number || ""
        });
      } else {
        message.error("Failed to fetch admin profile");
      }
    } catch (err) {
      console.error(err);
      message.error("Error loading admin profile");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      // Here you would typically make an API call to update the admin
      // For now, we'll just update local state
      setUser(prev => ({ ...prev, ...formData }));
      message.success("Profile updated successfully");
      setEditing(false);
    } catch (error) {
      message.error("Failed to update profile");
    }
  };

  const handleCancel = () => {
    setFormData({
      full_name: user?.full_name || "",
      email: user?.email || "",
      phone_number: user?.phone_number || ""
    });
    setEditing(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '80vh',
        flexDirection: 'column',
        gap: 16
      }}>
        <Spin size="large" />
        <Text type="secondary">Loading admin profile...</Text>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ 
        textAlign: "center", 
        padding: "100px 24px"
      }}>
        <Title level={4} style={{ marginBottom: 16 }}>Unable to load admin profile</Title>
        <Text type="secondary" style={{ marginBottom: 24, display: 'block' }}>
          We couldn't fetch your profile data. Please try again.
        </Text>
        <Button type="primary" onClick={fetchUserProfile}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div style={{ 
      maxWidth: 1000, 
      margin: '0 auto', 
      padding: screens.xs ? '16px' : '24px'
    }}>
      {/* Header Section */}
      <div style={{ marginBottom: 32 }}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Space align="center">
            <CrownOutlined style={{ fontSize: 24, color: '#faad14' }} />
            <Title level={2} style={{ margin: 0, fontWeight: 600 }}>
              Admin Profile
            </Title>
          </Space>
          <Text type="secondary" style={{ fontSize: 16 }}>
            Manage your administrator account and settings
          </Text>
        </Space>
      </div>

      <Row gutter={[24, 24]}>
        {/* Left Column - Admin Profile Card */}
        <Col xs={24} md={8}>
          <Card
            style={{
              borderRadius: 12,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              border: '1px solid #f0f0f0',
              height: '100%'
            }}
            bodyStyle={{ padding: 24 }}
          >
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Badge
                count={<CrownOutlined style={{ color: '#faad14', fontSize: 16 }} />}
                offset={[-10, 90]}
              >
                <Avatar
                  size={100}
                  src={profilavatar}
                  icon={!profilavatar && <UserOutlined />}
                  style={{
                    border: '4px solid #fff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    objectFit: 'cover',
                    marginBottom: 16
                  }}
                />
              </Badge>
              
              <Title level={4} style={{ margin: '8px 0 4px' }}>
                {user.full_name || "Administrator"}
              </Title>
              
              <Tag 
                color="gold" 
                icon={<CrownOutlined />}
                style={{ 
                  borderRadius: 12, 
                  padding: '4px 12px',
                  fontWeight: 500
                }}
              >
                {user.role?.replace("_", " ").toUpperCase() || "ADMIN"}
              </Tag>
              
              <Divider />
              
              {/* Admin Stats */}
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Statistic
                  title="Account Status"
                  value={user.is_active ? "Active" : "Inactive"}
                  valueStyle={{ 
                    color: user.is_active ? '#52c41a' : '#ff4d4f',
                    fontSize: '16px'
                  }}
                  prefix={user.is_active ? <SafetyCertificateOutlined /> : <ClockCircleOutlined />}
                />
                
                {user.is_verified && (
                  <Statistic
                    title="Verification"
                    value="Verified"
                    valueStyle={{ 
                      color: '#52c41a',
                      fontSize: '16px'
                    }}
                    prefix={<SafetyCertificateOutlined />}
                  />
                )}
              </Space>
            </div>

            <Divider />
            
            {!editing ? (
              <Button 
                type="primary" 
                block 
                icon={<EditOutlined />}
                onClick={handleEdit}
                style={{ height: 40 }}
              >
                Edit Profile
              </Button>
            ) : (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  type="primary" 
                  block 
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  style={{ height: 40 }}
                >
                  Save Changes
                </Button>
                <Button 
                  block 
                  icon={<CloseOutlined />}
                  onClick={handleCancel}
                  style={{ height: 40 }}
                >
                  Cancel
                </Button>
              </Space>
            )}
          </Card>
        </Col>

        {/* Right Column - Admin Information */}
        <Col xs={24} md={16}>
          <Card
            style={{
              borderRadius: 12,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              border: '1px solid #f0f0f0'
            }}
            bodyStyle={{ padding: 24 }}
          >
            {!editing ? (
              <Space direction="vertical" size={24} style={{ width: '100%' }}>
                {/* Personal Information */}
                <div>
                  <Title level={5} style={{ marginBottom: 16 }}>
                    <UserOutlined style={{ marginRight: 8 }} />
                    Personal Information
                  </Title>
                  <Descriptions column={1} bordered size="middle">
                    <Descriptions.Item label="Full Name">
                      <Text strong>{user.full_name || "Not set"}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Email Address">
                      <Space>
                        <Text>{user.email || "Not set"}</Text>
                        {user.is_verified && (
                          <Tag color="success" size="small">
                            Verified
                          </Tag>
                        )}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Phone Number">
                      <Text>{user.phone_number || "Not set"}</Text>
                    </Descriptions.Item>
                  </Descriptions>
                </div>

                {/* Admin Information */}
                <div>
                  <Title level={5} style={{ marginBottom: 16 }}>
                    <SettingOutlined style={{ marginRight: 8 }} />
                    Admin Information
                  </Title>
                  <Descriptions column={1} bordered size="middle">
                    <Descriptions.Item label="Admin Role">
                      <Tag color="gold" style={{ fontWeight: 500 }}>
                        {user.role?.replace("_", " ").toUpperCase() || "ADMIN"}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Account Status">
                      <Badge 
                        status={user.is_active ? "success" : "error"} 
                        text={user.is_active ? "Active" : "Inactive"}
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Login">
                      <Text>Today at 10:30 AM</Text>
                    </Descriptions.Item>
                  </Descriptions>
                </div>

                {/* System Access */}
                <div>
                  <Title level={5} style={{ marginBottom: 16 }}>
                    <DashboardOutlined style={{ marginRight: 8 }} />
                    System Access
                  </Title>
                  <Card size="small" style={{ background: '#fafafa' }}>
                    <Space wrap>
                      <Tag color="blue">Dashboard</Tag>
                      <Tag color="green">User Management</Tag>
                      <Tag color="purple">Content Management</Tag>
                      <Tag color="orange">Analytics</Tag>
                      <Tag color="red">System Settings</Tag>
                      <Tag color="cyan">Reports</Tag>
                    </Space>
                  </Card>
                </div>
              </Space>
            ) : (
              <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <Title level={5}>Edit Admin Profile</Title>
                
                <Form layout="vertical">
                  <Form.Item label="Full Name" required>
                    <Input
                      prefix={<UserOutlined />}
                      value={formData.full_name}
                      onChange={(e) => handleInputChange('full_name', e.target.value)}
                      placeholder="Enter your full name"
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item label="Email Address" required>
                    <Input
                      prefix={<MailOutlined />}
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter email address"
                      size="large"
                      type="email"
                    />
                  </Form.Item>

                  <Form.Item label="Phone Number">
                    <Input
                      prefix={<PhoneOutlined />}
                      value={formData.phone_number}
                      onChange={(e) => handleInputChange('phone_number', e.target.value)}
                      placeholder="Enter phone number"
                      size="large"
                    />
                  </Form.Item>
                </Form>
              </Space>
            )}
          </Card>
          
          {/* Admin Actions */}
          <Card
            title="Admin Actions"
            style={{
              marginTop: 24,
              borderRadius: 12,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              border: '1px solid #f0f0f0'
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Button 
                  icon={<TeamOutlined />} 
                  block 
                  size="large"
                  style={{ height: 50 }}
                >
                  Manage Users
                </Button>
              </Col>
              <Col xs={24} sm={12}>
                <Button 
                  icon={<DashboardOutlined />} 
                  block 
                  size="large"
                  style={{ height: 50 }}
                >
                  View Analytics
                </Button>
              </Col>
              <Col xs={24} sm={12}>
                <Button 
                  icon={<SettingOutlined />} 
                  block 
                  size="large"
                  style={{ height: 50 }}
                >
                  System Settings
                </Button>
              </Col>
              <Col xs={24} sm={12}>
                <Button 
                  type="primary" 
                  icon={<SaveOutlined />} 
                  block 
                  size="large"
                  style={{ height: 50 }}
                  onClick={fetchUserProfile}
                >
                  Refresh Profile
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Profile;